import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalToken, setOriginalToken] = useState(localStorage.getItem('originalToken'));

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedOriginalToken = localStorage.getItem('originalToken');
      
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
          setIsImpersonating(!!storedOriginalToken);
          setOriginalToken(storedOriginalToken);
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('originalToken');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.removeItem('originalToken');
    setToken(access_token);
    setUser(userData);
    setIsImpersonating(false);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('originalToken');
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
    setOriginalToken(null);
  };

  const impersonateUser = async (userId) => {
    try {
      // Save original token before impersonating
      const currentToken = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/auth/impersonate/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      
      const { access_token, user: userData } = response.data;
      
      // Store original token and set new impersonated token
      localStorage.setItem('originalToken', currentToken);
      localStorage.setItem('token', access_token);
      
      setOriginalToken(currentToken);
      setToken(access_token);
      setUser(userData);
      setIsImpersonating(true);
      
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const storedOriginalToken = localStorage.getItem('originalToken');
      
      if (storedOriginalToken) {
        // Restore original super admin session
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${storedOriginalToken}` }
        });
        
        localStorage.setItem('token', storedOriginalToken);
        localStorage.removeItem('originalToken');
        
        setToken(storedOriginalToken);
        setUser(response.data);
        setIsImpersonating(false);
        setOriginalToken(null);
        
        return response.data;
      }
    } catch (error) {
      // If original token is invalid, logout
      logout();
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        setUser(response.data);
        return response.data;
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const impersonateClient = async (clientId) => {
    try {
      const currentToken = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/auth/impersonate-client/${clientId}`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      const { access_token, user: userData } = response.data;
      localStorage.setItem('originalToken', currentToken);
      localStorage.setItem('token', access_token);
      setOriginalToken(currentToken);
      setToken(access_token);
      setUser(userData);
      setIsImpersonating(true);
      return userData;
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      getAuthHeaders,
      isImpersonating,
      impersonateUser,
      impersonateClient,
      stopImpersonation,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
